import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C1ChartCard } from '@/components/c1';

/**
 * The chart primitives the role panels are built from.
 *
 * VALIDATED, NOT CHOSEN BY EYE. #0057D2 / #0D9E9E / #E9730E clear the lightness band, the
 * chroma floor and colour-vision separation (worst adjacent pair ΔE 31.7 protan for the two
 * used together on fees). The amber sits at 2.95:1 against the surface, which is not a
 * dismissable warning — it obliges direct labels or a table view, so every form here carries
 * its numbers and every multi-series form offers a table.
 *
 * ONE SERIES NEEDS NO PALETTE AND NO LEGEND. Attendance over time, marks over time and the
 * grade distribution are each a single series: one colour, with the axis carrying identity.
 * A legend box naming the one thing the title already names is noise, and colouring the bars
 * of an ordinal A–F scale by six different hues would imply six unrelated categories rather
 * than one ordered scale.
 */
export const SERIES = ['#0057D2', '#0D9E9E', '#E9730E'] as const;

/** Shared by every form: the same data as a table, for anyone the marks do not serve. */
function TableToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="c1-tableToggle" onClick={onToggle} aria-pressed={on}>
      {on ? t('insights.showChart') : t('insights.showTable')}
    </button>
  );
}

/**
 * The drawing width in real CSS pixels.
 *
 * WHY MEASURE RATHER THAN SCALE A VIEWBOX. A fixed viewBox stretched to the card's
 * width scales the whole drawing, including the type and the stroke, by whatever
 * ratio the card happens to be — on a wide card the axis numbers come out visibly
 * smeared sideways and the line thickens. Drawing at the measured width means one
 * SVG unit is one pixel, so text is the size it says it is and a 2px line is 2px.
 */
function useMeasuredWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      if (measured > 0) setWidth(measured);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
}

/**
 * Round the axis outwards to whole steps.
 *
 * The scale must contain the threshold as well as the data, so a cohort sitting well
 * above the line still shows the line it must not fall below. An axis fitted only to
 * the values hides the one reference point that gives them meaning.
 */
function niceScale(values: number[], threshold: number | undefined, percentage: boolean) {
  const rawMax = Math.max(...values, threshold ?? Number.NEGATIVE_INFINITY);
  const rawMin = Math.min(...values, threshold ?? Number.POSITIVE_INFINITY);
  const step = percentage ? 10 : Math.max(1, 10 ** Math.floor(Math.log10(Math.max(rawMax, 1))) / 2);

  let min = Math.floor(rawMin / step) * step;
  let max = Math.ceil(rawMax / step) * step;
  if (percentage) {
    min = Math.max(0, min);
    max = Math.min(100, max);
  } else {
    min = Math.min(0, min);
  }
  // A flat series would otherwise divide by a zero span and collapse onto one line.
  if (max === min) max = min + step;

  return { min, max };
}

export interface TrendPoint {
  label: string;
  value: number | null;
}

/**
 * A line over time, with an optional threshold rule.
 *
 * WHY A LINE IS LEGITIMATE HERE and was not on the administrator's panel: attendance and
 * grade rows carry real timestamps, so this is measured history rather than a shape invented
 * to look analytical.
 *
 * GAPS ARE GAPS. A day with no register taken is `null` and BREAKS the line rather than
 * being interpolated across. Joining across a missing day draws a smooth descent into an
 * absence that was never recorded — the chart would be asserting something nobody measured.
 */
export function TrendLine({
  title,
  points,
  threshold,
  thresholdLabel,
  unit = '%',
  color = SERIES[0],
}: {
  title: string;
  points: TrendPoint[];
  threshold?: number;
  thresholdLabel?: string;
  unit?: string;
  color?: string;
}) {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover] = useState<number | null>(null);
  const clipId = useId();
  const gradientId = useId();
  const [plotRef, W] = useMeasuredWidth(560);

  const measured = points.filter((p): p is { label: string; value: number } => p.value !== null);

  const H = 200;
  const PAD = { top: 16, right: 16, bottom: 26, left: 40 };
  const plotWidth = Math.max(W - PAD.left - PAD.right, 1);
  const plotHeight = H - PAD.top - PAD.bottom;

  const { min, max } = niceScale(
    measured.map((p) => p.value),
    threshold,
    unit === '%',
  );
  const span = max - min;

  const x = (i: number) => PAD.left + (i / Math.max(points.length - 1, 1)) * plotWidth;
  const y = (v: number) => PAD.top + (1 - (v - min) / span) * plotHeight;

  // Built as separate runs so a null breaks the path instead of bridging it. A day with
  // no register taken is not a smooth descent into an absence nobody recorded.
  const runs: Array<Array<{ index: number; value: number }>> = [];
  let current: Array<{ index: number; value: number }> = [];
  points.forEach((point, index) => {
    if (point.value === null) {
      if (current.length > 0) runs.push(current);
      current = [];
      return;
    }
    current.push({ index, value: point.value });
  });
  if (current.length > 0) runs.push(current);

  const linePath = (run: Array<{ index: number; value: number }>) =>
    run.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.index)} ${y(p.value)}`).join(' ');

  // How many dates the axis can hold, budgeted from the width it actually has: six
  // across a desktop card, two on a phone. A fixed count collides into a smear as
  // soon as the card is narrow.
  const maxTicks = Math.max(2, Math.min(6, Math.floor(plotWidth / 80)));
  const tickEvery = Math.max(1, Math.ceil(points.length / maxTicks));
  const xTicks = points
    .map((point, index) => ({ point, index }))
    .filter(({ index }) => index % tickEvery === 0 || index === points.length - 1);

  const latest = measured.length > 0 ? measured[measured.length - 1].value : null;
  const summary = `${title}: ${latest}${unit} ${t('insights.mostRecent')}`;

  if (measured.length === 0) {
    return (
      <C1ChartCard title={title}>
        <p className="c1-chartEmpty">{t('insights.noneRecorded')}</p>
      </C1ChartCard>
    );
  }

  return (
    <C1ChartCard
      title={title}
      action={<TableToggle on={showTable} onToggle={() => setShowTable((v) => !v)} />}
    >
      {showTable ? (
        <div className="insight-tableScroll">
          <table className="insight-table">
            <caption className="sr-only">{title}</caption>
            <thead>
              <tr>
                <th scope="col">{t('insights.date')}</th>
                <th scope="col">{title}</th>
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.label}>
                  <th scope="row">{point.label}</th>
                  <td>{point.value === null ? '—' : `${point.value}${unit}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="insight-plot" ref={plotRef}>
          {/* role="img" with a summary: a screen reader gets the headline figure rather
              than being walked through a path element it cannot interpret. The table
              behind the toggle is the full detail. */}
          <svg
            className="insight-svg"
            width="100%"
            height={H}
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={summary}
            onMouseLeave={() => setHover(null)}
          >
            <defs>
              <clipPath id={clipId}>
                <rect x={PAD.left} y={0} width={plotWidth} height={H} />
              </clipPath>
              {/* The area is a tint of the line, fading out downwards: it gives the
                  line a body to read against without competing with it. */}
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.18} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>

            {[min, Math.round((min + max) / 2), max].map((tick) => (
              <g key={tick}>
                <line
                  x1={PAD.left}
                  x2={W - PAD.right}
                  y1={y(tick)}
                  y2={y(tick)}
                  className="insight-grid"
                />
                <text
                  x={PAD.left - 8}
                  y={y(tick)}
                  className="insight-axisText insight-axisText--y"
                >
                  {tick}
                </text>
              </g>
            ))}

            {xTicks.map(({ point, index }) => (
              <text
                key={point.label}
                x={x(index)}
                y={H - 8}
                className="insight-axisText"
                textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}
              >
                {point.label}
              </text>
            ))}

            {threshold !== undefined && (
              <>
                {/* Dashed and labelled, so it never reads as a second data series. */}
                <line
                  x1={PAD.left}
                  x2={W - PAD.right}
                  y1={y(threshold)}
                  y2={y(threshold)}
                  className="insight-threshold"
                />
                <text x={W - PAD.right} y={y(threshold) - 6} className="insight-thresholdText">
                  {thresholdLabel ?? threshold}
                </text>
              </>
            )}

            {runs.map((run) => (
              <g key={`run-${run[0].index}`} clipPath={`url(#${clipId})`}>
                {run.length > 1 && (
                  <path
                    d={`${linePath(run)} L${x(run[run.length - 1].index)} ${y(min)} L${x(run[0].index)} ${y(min)} Z`}
                    fill={`url(#${gradientId})`}
                  />
                )}
                <path
                  d={linePath(run)}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* A single measurement has no line to draw, so it is marked as a point
                    rather than disappearing. */}
                {run.length === 1 && (
                  <circle cx={x(run[0].index)} cy={y(run[0].value)} r={3.5} fill={color} />
                )}
              </g>
            ))}

            {hover !== null && points[hover]?.value != null && (
              <g>
                <line
                  x1={x(hover)}
                  x2={x(hover)}
                  y1={PAD.top}
                  y2={H - PAD.bottom}
                  className="insight-hoverLine"
                />
                <circle
                  cx={x(hover)}
                  cy={y(points[hover].value)}
                  r={4.5}
                  fill={color}
                  className="insight-dot"
                />
              </g>
            )}

            {/* Invisible full-height columns, so the pointer target is the whole band
                rather than a 2px line nobody can hit. */}
            {points.map((point, index) => (
              <rect
                key={point.label}
                x={x(index) - plotWidth / Math.max(points.length - 1, 1) / 2}
                y={0}
                width={plotWidth / Math.max(points.length - 1, 1)}
                height={H}
                fill="transparent"
                onMouseEnter={() => setHover(index)}
              />
            ))}
          </svg>

          <p className="insight-hoverReadout" aria-hidden="true">
            {hover !== null && points[hover]
              ? `${points[hover].label}: ${points[hover].value === null ? '—' : `${points[hover].value}${unit}`}`
              : `${t('insights.mostRecent')}: ${latest}${unit}`}
          </p>
        </div>
      )}
    </C1ChartCard>
  );
}

export interface CategoryDatum {
  key: string;
  label: string;
  value: number;
}

/**
 * A horizontal bar for magnitudes across a handful of categories.
 *
 * ONE COLOUR FOR EVERY BAR. These are values of one measure, not separate series — painting
 * each a different hue would invent a distinction the data does not contain. Length carries
 * the comparison; the label carries identity.
 */
export function CategoryBars({
  title,
  data,
  color = SERIES[0],
  emptyMessage,
}: {
  title: string;
  data: CategoryDatum[];
  color?: string;
  emptyMessage?: string;
}) {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);
  const max = Math.max(...data.map((d) => d.value), 1);

  if (data.length === 0) {
    return (
      <C1ChartCard title={title}>
        <p className="c1-chartEmpty">{emptyMessage ?? t('insights.noneRecorded')}</p>
      </C1ChartCard>
    );
  }

  return (
    <C1ChartCard
      title={title}
      action={<TableToggle on={showTable} onToggle={() => setShowTable((v) => !v)} />}
    >
      {showTable ? (
        <table className="insight-table">
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr>
              <th scope="col">{t('insights.group')}</th>
              <th scope="col">{t('insights.count')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.key}>
                <th scope="row">{d.label}</th>
                <td>{d.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <ul className="insight-bars">
          {data.map((d) => (
            <li key={d.key} className="insight-bar">
              <span className="insight-barLabel">{d.label}</span>
              <span className="insight-barTrack">
                <span
                  className="insight-barFill"
                  style={{ width: `${(d.value / max) * 100}%`, background: color }}
                  aria-hidden="true"
                />
              </span>
              <span className="insight-barValue">{d.value}</span>
            </li>
          ))}
        </ul>
      )}
    </C1ChartCard>
  );
}

/**
 * A single value against the threshold that gives it meaning.
 *
 * NOT A GAUGE WITH A NEEDLE, and not a percentage in isolation. Attendance governs exam
 * eligibility, so the question is never "how high is it" but "which side of the line is it
 * on" — and that is a position, which a bar with a marked rule answers directly. 78% and 72%
 * look nearly identical on a dial and are categorically different situations here.
 */
export function ThresholdMeter({
  title,
  value,
  threshold,
  thresholdLabel,
  caption,
}: {
  title: string;
  value: number | null;
  threshold: number;
  thresholdLabel: string;
  caption?: string;
}) {
  const { t } = useTranslation();

  if (value === null) {
    return (
      <C1ChartCard title={title}>
        {/* Not 0%. Nothing recorded and total absence are opposite situations. */}
        <p className="c1-chartEmpty">{t('insights.noneRecorded')}</p>
      </C1ChartCard>
    );
  }

  const below = value < threshold;

  return (
    <C1ChartCard title={title} caption={caption}>
      <p className={`insight-heroValue ${below ? 'insight-heroValue--risk' : ''}`}>
        {value}
        <span className="insight-heroUnit">%</span>
      </p>
      <div
        className="insight-meter"
        role="meter"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${title}: ${value}%, ${thresholdLabel}`}
      >
        <span
          className={`insight-meterFill ${below ? 'insight-meterFill--risk' : ''}`}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
        <span className="insight-meterRule" style={{ left: `${threshold}%` }} aria-hidden="true" />
      </div>
      {/* The word carries the verdict, not just the colour — the rule is invisible to
          someone who cannot separate the two hues. */}
      <p className={`insight-meterCaption ${below ? 'insight-meterCaption--risk' : ''}`}>
        {below ? t('insights.belowThreshold', { threshold }) : t('insights.aboveThreshold', { threshold })}
      </p>
    </C1ChartCard>
  );
}

/**
 * Two parts of one total, on one bar.
 *
 * A stacked bar rather than two bars or a pie: the question is what share has been
 * collected, and a share is read from a common length. The 2px gap between the segments is
 * the surface showing through, so the boundary stays visible when the two colours are hard
 * to tell apart.
 */
export function SplitBar({
  title,
  parts,
  format,
}: {
  title: string;
  parts: Array<{ key: string; label: string; value: number; color: string }>;
  format: (value: number) => string;
}) {
  const total = parts.reduce((sum, part) => sum + part.value, 0);

  if (total <= 0) {
    return (
      <C1ChartCard title={title}>
        <p className="c1-chartEmpty">{format(0)}</p>
      </C1ChartCard>
    );
  }

  return (
    <C1ChartCard title={title}>
      <div className="insight-split" role="img" aria-label={parts.map((p) => `${p.label} ${format(p.value)}`).join(', ')}>
        {parts
          .filter((part) => part.value > 0)
          .map((part) => (
            <span
              key={part.key}
              className="insight-splitSegment"
              style={{ width: `${(part.value / total) * 100}%`, background: part.color }}
            />
          ))}
      </div>
      {/* A legend, because there are two series — and the amounts beside it, because the
          amber segment cannot be relied on to be distinguished by colour alone. */}
      <ul className="insight-legend">
        {parts.map((part) => (
          <li key={part.key} className="insight-legendItem">
            <span className="insight-legendSwatch" style={{ background: part.color }} aria-hidden="true" />
            <span className="insight-legendLabel">{part.label}</span>
            <span className="insight-legendValue">{format(part.value)}</span>
          </li>
        ))}
      </ul>
    </C1ChartCard>
  );
}
